// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// AL-59 — dictation for the Ask LodgIQ composer, on the browser's own speech
// engine (Web Speech API). No backend, no tokens, no vendor key.
//
// PRIVACY: recognition is NOT on-device in most browsers — Chrome streams the
// captured audio to Google's speech service, Safari to Apple's. The mic is
// therefore opt-in per click, never listens in the background, and stops on
// silence. Unsupported browsers (Firefox) get `supported: false` so the caller
// can hide the button entirely rather than showing something that can't work.
//
// The hook only produces TEXT. Sending stays a deliberate user action, because a
// misheard rate in an auto-sent question is worse than one extra click.

import { useCallback, useEffect, useRef, useState } from "react";

const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

// Silence after which we stop on our own (browsers vary wildly on this).
const SILENCE_MS = 2200;
// Hard cap so a forgotten open mic can't run forever.
const MAX_MS = 60000;

/**
 * @param {(text: string, meta: {durationMs: number}) => void} onFinal
 *        called with the completed transcript when recognition ends
 * @param {(text: string) => void} onInterim
 *        called continuously with the in-progress transcript (may be "")
 */
export default function useVoiceInput({ onFinal, onInterim, lang } = {}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");        // "" | "denied" | "nospeech" | "failed"

  const recRef = useRef(null);
  const finalRef = useRef("");
  const silenceRef = useRef(null);
  const maxRef = useRef(null);
  const startedAtRef = useRef(0);
  // keep the latest callbacks without re-creating the recogniser
  const cbRef = useRef({ onFinal, onInterim });
  cbRef.current = { onFinal, onInterim };

  const clearTimers = () => {
    if (silenceRef.current) clearTimeout(silenceRef.current);
    if (maxRef.current) clearTimeout(maxRef.current);
    silenceRef.current = null;
    maxRef.current = null;
  };

  const stop = useCallback(() => {
    clearTimers();
    const rec = recRef.current;
    if (rec) {
      try { rec.stop(); } catch (_) { /* already stopped */ }
    }
  }, []);

  const start = useCallback(() => {
    if (!SR || recRef.current) return;
    setError("");
    finalRef.current = "";
    startedAtRef.current = Date.now();

    let rec;
    try {
      rec = new SR();
    } catch (_) {
      setError("failed");
      return;
    }
    rec.continuous = true;          // let people pause mid-sentence
    rec.interimResults = true;      // stream words into the box as they speak
    rec.maxAlternatives = 1;
    rec.lang = lang || (typeof navigator !== "undefined" && navigator.language) || "en-US";

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const txt = (res[0] && res[0].transcript) || "";
        if (res.isFinal) finalRef.current += txt;
        else interim += txt;
      }
      const { onInterim: oi } = cbRef.current;
      if (oi) oi((finalRef.current + interim).trim());
      // reset the silence window on every utterance
      if (silenceRef.current) clearTimeout(silenceRef.current);
      silenceRef.current = setTimeout(() => stop(), SILENCE_MS);
    };

    rec.onerror = (event) => {
      const code = event && event.error;
      if (code === "not-allowed" || code === "service-not-allowed") setError("denied");
      else if (code === "no-speech") setError("nospeech");
      else if (code !== "aborted") setError("failed");
    };

    rec.onend = () => {
      clearTimers();
      recRef.current = null;
      setListening(false);
      const text = finalRef.current.trim();
      finalRef.current = "";
      const { onFinal: of } = cbRef.current;
      if (text && of) of(text, { durationMs: Date.now() - startedAtRef.current });
    };

    try {
      rec.start();
    } catch (_) {
      recRef.current = null;
      setError("failed");
      return;
    }
    recRef.current = rec;
    setListening(true);
    maxRef.current = setTimeout(() => stop(), MAX_MS);
  }, [lang, stop]);

  const toggle = useCallback(() => {
    if (recRef.current) stop();
    else start();
  }, [start, stop]);

  // never leave the mic open when the chat closes / unmounts
  useEffect(() => () => {
    clearTimers();
    const rec = recRef.current;
    recRef.current = null;
    if (rec) {
      try { rec.abort(); } catch (_) { /* noop */ }
    }
  }, []);

  return { supported: !!SR, listening, error, start, stop, toggle, clearError: () => setError("") };
}
